import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";

const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600";

export const FIELD_GROUPS = [
  {
    title: "Basics",
    fields: [
      ["nickname", "Nickname"], ["year", "Year *"], ["make", "Make *"], ["model", "Model *"],
      ["trim", "Trim"], ["color", "Color"], ["license_plate", "License Plate"],
      ["vin", "VIN"], ["mileage", "Current Mileage / Odometer"],
    ],
  },
  {
    title: "Specs",
    fields: [
      ["engine", "Engine"], ["tire_size", "Tire Size"], ["oil_type", "Oil Type"],
      ["oil_capacity", "Oil Capacity"], ["transmission_type", "Transmission Type"],
      ["fuel_type", "Fuel Type"], ["drive_type", "Drive Type"],
      ["purchase_date", "Purchase Date"], ["warranty_expiration", "Warranty Expiration"],
    ],
  },
  {
    title: "Service History Dates",
    fields: [
      ["battery_installed_date", "Battery Installed"], ["air_filter_date", "Engine Air Filter"],
      ["cabin_filter_date", "Cabin Filter"], ["brake_service_date", "Brake Service"],
      ["coolant_service_date", "Coolant Service"], ["transmission_service_date", "Transmission Service"],
      ["spark_plug_date", "Spark Plugs"], ["serpentine_belt_date", "Serpentine Belt"],
      ["timing_belt_date", "Timing Belt"], ["differential_service_date", "Differential Service"],
      ["transfer_case_service_date", "Transfer Case Service"], ["brake_fluid_date", "Brake Fluid"],
      ["power_steering_date", "Power Steering"], ["tire_rotation_date", "Tire Rotation"],
      ["alignment_date", "Alignment"],
    ],
  },
  {
    title: "Renewals",
    fields: [
      ["registration_renewal", "State Registration Renewal"],
      ["insurance_renewal", "Insurance Renewal"], ["emissions_due", "Emissions Due"],
    ],
  },
];

const DATE_FIELDS = new Set([
  "purchase_date", "warranty_expiration", "battery_installed_date", "air_filter_date",
  "cabin_filter_date", "brake_service_date", "coolant_service_date", "transmission_service_date",
  "spark_plug_date", "serpentine_belt_date", "timing_belt_date", "differential_service_date",
  "transfer_case_service_date", "brake_fluid_date", "power_steering_date", "tire_rotation_date",
  "alignment_date", "registration_renewal", "insurance_renewal", "emissions_due",
]);

export const VehicleDialog = ({ open, onOpenChange, vehicle, onSave, saving }) => {
  const [form, setForm] = useState(vehicle || {});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{vehicle?.vehicle_id ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          {FIELD_GROUPS.map((group) => (
            <div key={group.title}>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">{group.title}</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {group.fields.map(([id, label]) => (
                  <div key={id} className="space-y-1">
                    <Label htmlFor={`veh-${id}`} className="text-zinc-400 text-xs">{label}</Label>
                    <Input
                      id={`veh-${id}`}
                      data-testid={`vehicle-input-${id}`}
                      type={DATE_FIELDS.has(id) ? "date" : "text"}
                      required={["year", "make", "model"].includes(id)}
                      className={inputCls}
                      value={form[id] || ""}
                      onChange={set(id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <DialogFooter>
            <button
              data-testid="vehicle-save-button"
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm px-6 py-2.5 font-semibold transition-colors"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Vehicle
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
